import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import Modal from './EmpresasUnidadesOrgModal';

const PAGE_SIZE = 10;
const nombreTabla = 'Empresas - Unidades Organizacionales';
const endpoint = ENDPOINTS.empresasUnidadesOrg;
const endpointById = ENDPOINTS.empresasUnidadesOrgById;
const modelo = Models.empresasUnidadesOrg;

export default function EmpresasUnidadesOrgCatalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [page, setPage] = useState(1);

  const [empresas, setEmpresas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint);
      setRows((res.data || []).map((x) => modelo(x)));
    } catch (err) {
      console.error(err);
      alert('Error al cargar las relaciones Empresa-UO');
    } finally {
      setLoading(false);
    }
  };

  const loadCombos = async () => {
    try {
      const [resEmpresas, resUnidades] = await Promise.all([
        axios.get(ENDPOINTS.empresas),
        axios.get(ENDPOINTS.unidadesOrg),
      ]);
      setEmpresas((resEmpresas.data || []).map((x) => Models.empresas(x)));
      setUnidades((resUnidades.data || []).map((x) => Models.unidadesOrg(x)));
    } catch (err) {
      console.error('Error al cargar catálogos secundarios', err);
    }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadCombos(); }, []);

  const empresasMap = useMemo(() => {
    const map = {};
    empresas.forEach((e) => { map[e.idEmpresa] = e.nombre || `Empresa #${e.idEmpresa}`; });
    return map;
  }, [empresas]);

  const unidadesMap = useMemo(() => {
    const map = {};
    unidades.forEach((u) => { map[u.idUnidadOrganizacional] = u.nombre || `Unidad #${u.idUnidadOrganizacional}`; });
    return map;
  }, [unidades]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const empresa = (empresasMap[r.idEmpresa] || '').toLowerCase();
      const unidad = (unidadesMap[r.idUnidadOrganizacional] || '').toLowerCase();
      return empresa.includes(q) || unidad.includes(q);
    });
  }, [rows, query, empresasMap, unidadesMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
  };

  const handleEdit = (id) => {
    const r = rows.find((x) => x.idEmpresasUnidadesOrg === id);
    if (!r) {
      alert('No se encontró la relación en memoria');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
  };

  const askDelete = (row) => {
    setDeleteTarget({
      id: row.idEmpresasUnidadesOrg,
      name: `${empresasMap[row.idEmpresa] || 'Empresa'} · ${unidadesMap[row.idUnidadOrganizacional] || 'Unidad'}`,
    });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      await loadData();
    } catch (err) {
      console.error(err);
      alert('Error al eliminar la relación');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        await axios.post(endpoint, payload);
      } else if (selectedId != null) {
        await axios.put(endpoint, { ...payload, idEmpresasUnidadesOrg: selectedId });
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      console.error(err);
      alert('Error al guardar la relación');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button type="button" className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`} onClick={onClick} disabled={disabled}>
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>
          <input
            className="roles-search"
            placeholder="Buscar por empresa o unidad..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Unidad Organizacional</th>
                  <th>Activo</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idEmpresasUnidadesOrg}>
                    <td>{normalizeValue(empresasMap[r.idEmpresa])}</td>
                    <td>{normalizeValue(unidadesMap[r.idUnidadOrganizacional])}</td>
                    <td>{r.activo ? 'Sí' : 'No'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--edit"
                        onClick={() => handleEdit(r.idEmpresasUnidadesOrg)}
                        aria-label="Editar"
                        title="Editar"
                      >
                        <img src={sigmaEdit} alt="Editar" className="rk-ico" />
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--del"
                        onClick={() => askDelete(r)}
                        aria-label="Borrar"
                        title="Borrar"
                      >
                        <img src={sigmaDelete} alt="Borrar" className="rk-ico" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={5}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map((n) => (
              <PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />
            ))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              empresas={empresas}
              unidades={unidades}
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
